/**
 * Picking a rendition for a congress document image, and reading its crop.
 *
 * `Image.sizes` is produced ASYNCHRONOUSLY by the responsive-versions job, so it
 * is null right after upload and fills in later. That is a NORMAL state, not an
 * error: every reader here falls back to the original `url`, so a
 * just-uploaded document renders correctly (just heavier) and never renders
 * nothing. If the job fails outright, the fallback is permanent — degraded, not
 * broken.
 */

/** The `sizes` shape as it arrives on the wire (a rung, or null before the job). */
type Rendition = { file: string; width: number; height: number };
export type DocumentImageSizes = {
  icon: Rendition;
  large: Rendition;
  medium: Rendition;
  original: Rendition;
  small: Rendition;
  thumbnail: Rendition;
  thumbnailPng: Rendition | null;
} | null;

/**
 * The 16:9 card image.
 *
 * `large` (1600px bound) rather than `medium` (760), and the reason is
 * arithmetic rather than caution. The card is ~350 CSS px wide, so ~700px at 2×
 * DPR — but the card shows a CROP, a sub-rectangle of the rendition. On a wide
 * floor plan (say 4000×1200) `medium` bounds to 760×228, and a tight crop of
 * that is well under 700px wide and visibly soft. `large` is the first rung that
 * survives being cropped, and is still an order of magnitude under a
 * multi-megabyte original.
 */
export function cardImageSrc(url: string, sizes: DocumentImageSizes): string {
  return sizes === null ? url : sizes.large.file;
}

/**
 * A PDF card's small thumbnail — it renders at ~64–96px, so `small` (400px) is
 * already generous. Before this, the card downloaded the full original to draw
 * a thumbnail that size.
 */
export function thumbnailSrc(url: string, sizes: DocumentImageSizes): string {
  return sizes === null ? url : sizes.small.file;
}

/**
 * The lightbox image: the ORIGINAL, always.
 *
 * Anything smaller blurs under the 4× zoom a floor plan invites, and the whole
 * point of storing crop COORDINATES rather than a cropped file is that the
 * uncropped original survives for exactly this.
 */
export function lightboxSrc(url: string, sizes: DocumentImageSizes): string {
  return sizes === null ? url : sizes.original.file;
}

/**
 * The image's intrinsic aspect ratio, for RESERVING the lightbox's box before
 * anything is decoded.
 *
 * The reasoning: an `<img>` with `height: auto` and no decoded content resolves
 * to height ZERO, so the layout morph had a target box that was measurable and
 * WRONG — full width, no height — and jumped once decode gave it a natural
 * height. Knowing the ratio up front makes the box correct on the first
 * animation frame.
 *
 * UNVERIFIED, and unverifiABLE while the lightbox opens on the rendition the card
 * has already decoded — the harness sees no difference with this removed, even
 * under 20x CPU throttling, because that fix makes this one's condition
 * unreachable. The two are COUPLED, and that is the argument for keeping it:
 * without it, the system would depend permanently on the lightbox never changing
 * which src it opens with, and nothing would catch it if someone did. The
 * zero-height resolution above is spec, not speculation. See the note in
 * `playwright/lightbox-frames.spec.ts`.
 *
 * Every rendition in `sizes` carries width+height, and they all share the
 * original's ratio, so `original` is just the most obviously correct one to read.
 * Returns null when `sizes` is null (renditions are generated asynchronously and
 * nothing else stores the dimensions) — the caller then falls back to measuring
 * the card's already-decoded image, which is the only other honest source.
 */
export function imageAspectRatio(sizes: DocumentImageSizes): number | null {
  if (sizes === null) return null;
  if (sizes.original.height === 0) return null;
  return sizes.original.width / sizes.original.height;
}

/** Crop as 0..1 fractions of the original; null means centred. */
export type DocumentCrop = {
  x: number;
  y: number;
  width: number;
  height: number;
} | null;

/**
 * The crop as CSS on a fixed-ratio box: scale the image up by 1/crop.width and
 * offset it so the crop's top-left lands at the box's top-left.
 *
 * Fractions are what make this work against any rendition — the same numbers
 * apply to `large` on the reader and to the original in the admin preview before
 * renditions exist, so the preview is truthful from the moment of upload.
 */
export function cropStyle(crop: DocumentCrop): React.CSSProperties {
  if (crop === null) {
    // Centred: fill the box and let object-fit do the cropping.
    return { width: "100%", height: "100%", objectFit: "cover" };
  }
  return {
    position: "absolute",
    width: `${(100 / crop.width).toString()}%`,
    height: `${(100 / crop.height).toString()}%`,
    left: `${((-crop.x * 100) / crop.width).toString()}%`,
    top: `${((-crop.y * 100) / crop.height).toString()}%`,
    maxWidth: "none",
  };
}
